import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parse, isAfter, isBefore, addDays, startOfDay, endOfDay, parseISO, isValid, startOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { 
  Users, 
  Calendar, 
  DollarSign, 
  UserPlus, 
  TrendingUp, 
  Clock, 
  ChevronRight,
  ArrowUpRight,
  Users2,
  RefreshCw,
  Search,
  HelpCircle,
  CheckCircle2,
  Activity,
  Pencil,
  Trash2,
  Handshake,
  BookOpen,
  Book,
  BarChart3,
  Trophy,
  FileText,
  Home,
  CheckSquare,
  Bell,
  MessageSquare,
  ArrowDownLeft,
  Baby,
  Send,
  MessageCircle,
  Phone,
  User,
  Sparkles,
  Cake,
  Heart,
  Crown,
  UserCheck,
  UserX,
  Hash,
  ArrowLeft,
  Star,
  Church,
  Reply,
  CheckCircle,
  Target,
  ListTodo,
  ClipboardList,
  Plus,
  Edit,
  Trash,
  User2,
  UserPlus2,
  AlertCircle,
  Clock4,
  Flag,
  Gift,
  AlertTriangle,
  Share,
  ChevronLeft,
  ChevronDown,
  Filter,
  MoreVertical,
  Eye,
  Download
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { addMember, updateMember, deleteMember, getMemberAttendance, updateDonation, deleteDonation, addEventAttendance, getEventAttendance, getVolunteerStats, getMembers } from '../lib/data';
import { getAlertStats } from '../lib/alertsService';
import { smsService } from '../lib/smsService';
import { dashboardService, clearAllCaches } from '../lib/dashboardService';
import { userCacheService } from '../lib/userCache';
import { SmartInsightsQueries } from '../lib/aiInsightsService';
import { familyService } from '../lib/familyService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from '@/lib/authContext';
import { Label } from '@/components/ui/label';
import { formatName, getInitials } from '@/lib/utils/formatters';
import LeadershipVerse from '@/components/LeadershipVerse';
import { PermissionFeature } from '@/components/PermissionGuard';
import { PERMISSIONS } from '@/lib/permissions';
import { TaskCreationModal } from '@/components/TaskCreationModal';

export function MobileDashboard() {
  console.log('MobileDashboard: Component rendered');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pullToRefreshY, setPullToRefreshY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [activeTab, setActiveTab] = useState('focus');
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Dashboard data states
  const [dashboardData, setDashboardData] = useState({
    totalMembers: 0,
    recentMembers: [],
    upcomingEvents: [],
    recentDonations: [],
    attendanceStats: {},
    volunteerStats: {},
    alertStats: {},
    quickActions: [],
    recentActivity: []
  });

  // Analytics stats - same as desktop
  const [stats, setStats] = useState({
    sundayServiceRate: 0,
    eventsThisMonth: 0,
    averageEventsPerMonth: 0,
    eventsNeedingVolunteers: 0,
    totalTasks: 0,
    overdueTasks: 0,
    eventsStillNeedingVolunteers: 0,
    totalSMSConversations: 0,
    totalSMSMessages: 0,
    totalUpcoming: 0,
    upcomingMembers: [],
    totalFamilies: 0,
    membersInFamilies: 0,
    totalPeople: 0,
    sundayServiceEvents: 0,
    bibleStudyEvents: 0,
    fellowshipEvents: 0,
    thisWeek: 0,
    lastWeek: 0,
    growthRate: 0,
    inactiveMembers: 0
  });

  // People data for visitor calculations
  const [people, setPeople] = useState([]);
  const [atRiskMembers, setAtRiskMembers] = useState([]);
  const [donationTrendAnalysis, setDonationTrendAnalysis] = useState({});

  // Advanced Analytics Card Dialog state (same as desktop)
  const [selectedCardType, setSelectedCardType] = useState(null);
  const [isCardDialogOpen, setIsCardDialogOpen] = useState(false);
  const [isLoadingAtRiskMembers, setIsLoadingAtRiskMembers] = useState(false);

  // Task Creation Modal state (same as desktop)
  const [isTaskCreationModalOpen, setIsTaskCreationModalOpen] = useState(false);
  const [currentTaskSuggestion, setCurrentTaskSuggestion] = useState(null);

  // Quick actions for mobile
  const quickActions = [
    {
      id: 'members',
      title: 'Members',
      description: 'View members',
      icon: UserPlus,
      color: 'bg-blue-500',
      href: '/members',
      permission: PERMISSIONS.MEMBERS_EDIT
    },
    {
      id: 'kids-checkin',
      title: 'Kids Check-In',
      description: 'Check in kids',
      icon: Baby,
      color: 'bg-pink-500',
      href: '/children-check-in',
      permission: PERMISSIONS.CHILDREN_VIEW
    },
    {
      id: 'events',
      title: 'Events',
      description: 'View events',
      icon: Calendar,
      color: 'bg-green-500',
      href: '/events',
      permission: PERMISSIONS.EVENTS_EDIT
    },
    {
      id: 'messaging',
      title: 'Messaging',
      description: 'Send messages',
      icon: MessageSquare,
      color: 'bg-teal-500',
      href: '/messaging',
      permission: PERMISSIONS.SETTINGS_VIEW
    },
    {
      id: 'donations',
      title: 'Donations',
      description: 'View donations',
      icon: DollarSign,
      color: 'bg-yellow-500',
      href: '/donations',
      permission: PERMISSIONS.DONATIONS_VIEW
    },
    {
      id: 'tasks',
      title: 'Tasks',
      description: 'Manage tasks',
      icon: ClipboardList,
      color: 'bg-orange-500',
      href: '/tasks',
      permission: PERMISSIONS.TASKS_VIEW
    }
  ];

  // Generate comprehensive recent activity
  const generateRecentActivity = useCallback((data) => {
    const activities = [];
    
    // Add recent members
    if (data.members?.recent) {
      data.members.recent.slice(0, 2).forEach(member => {
        // Check if member is active or visitor based on status
        const isActiveMember = member.status === 'active' || member.status === 'member';
        const description = isActiveMember ? 'New member joined' : 'New visitor added';
        const badge = isActiveMember ? 'Member' : 'Visitor';
        
        activities.push({
          id: `member-${member.id}`,
          type: 'member',
          title: formatName(member.firstname || '', member.lastname || ''),
          description: description,
          icon: UserPlus,
          color: isActiveMember ? 'bg-blue-500' : 'bg-purple-500',
          badge: badge,
          timestamp: member.created_at || new Date().toISOString(),
          initials: getInitials(member.firstname || '', member.lastname || ''),
          href: `/members/${member.id}`
        });
      });
    }
    
    // Add recent events
    if (data.events?.upcoming) {
      data.events.upcoming.slice(0, 2).forEach(event => {
        activities.push({
          id: `event-${event.id}`,
          type: 'event',
          title: event.title,
          description: 'Event scheduled',
          icon: Calendar,
          color: 'bg-green-500',
          badge: 'Event',
          timestamp: event.created_at || event.start_date,
          initials: event.title?.charAt(0) || 'E',
          href: `/events/${event.id}`
        });
      });
    }
    
    // Add recent donations
    if (data.donations?.recent) {
      data.donations.recent.slice(0, 2).forEach(donation => {
        activities.push({
          id: `donation-${donation.id}`,
          type: 'donation',
          title: `$${donation.amount}`,
          description: 'Donation received',
          icon: DollarSign,
          color: 'bg-yellow-500',
          badge: 'Gift',
          timestamp: donation.created_at || new Date().toISOString(),
          initials: '$',
          href: '/donations'
        });
      });
    }
    
    // Sort by timestamp (most recent first)
    return activities
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 5); // Limit to 5 most recent activities
  }, []);

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('MobileDashboard: Loading dashboard data...');
      const data = await dashboardService.getDashboardData();
      console.log('MobileDashboard: Dashboard data loaded:', data);
      
      // Extract data from the consolidated response (same as desktop)
      const { members, donations: donationsData, events: eventsData, tasks, sms, celebrations, attendance, family } = data;
      
      // Transform members data to match existing structure (same as desktop)
      const transformedPeople = members.all?.map(person => ({
        ...person,
        firstName: person.firstname || '',
        lastName: person.lastname || '',
        joinDate: person.join_date || '',
        createdAt: person.created_at,
        updatedAt: person.updated_at
      })) || [];

      // Set stats using the same logic as desktop
      setStats({
        sundayServiceRate: attendance.sundayServiceRate || 0,
        eventsThisMonth: eventsData.stats.thisMonth || 0,
        averageEventsPerMonth: eventsData.stats.averagePerMonth || 0,
        eventsNeedingVolunteers: eventsData.stats.needingVolunteers || 0,
        totalTasks: tasks.stats.total || 0,
        overdueTasks: tasks.stats.overdue || 0,
        eventsStillNeedingVolunteers: 0, // Placeholder - not in current data
        totalSMSConversations: sms.totalConversations || 0,
        totalSMSMessages: sms.totalMessages || 0,
        totalUpcoming: celebrations.totalUpcoming || 0,
        upcomingMembers: celebrations.upcomingMembers || [],
        totalFamilies: family.totalFamilies || 0,
        membersInFamilies: family.membersInFamilies || 0,
        totalPeople: members.counts.total || 0,
        sundayServiceEvents: attendance.sundayServiceEvents || 0,
        bibleStudyEvents: attendance.bibleStudyEvents || 0,
        fellowshipEvents: attendance.fellowshipEvents || 0,
        thisWeek: donationsData.stats.thisWeek || 0,
        lastWeek: donationsData.stats.lastWeek || 0,
        growthRate: donationsData.stats.growthRate || 0,
        inactiveMembers: members.counts.inactive || 0
      });

      // Set people data for visitor calculations
      setPeople(transformedPeople);

      // Set donation trend analysis (same as desktop)
      setDonationTrendAnalysis(donationsData.trendAnalysis || {});

      // Load at-risk members (same as desktop)
      try {
        if (data.organizationId) {
          const atRiskData = await SmartInsightsQueries.getAtRiskMembers(data.organizationId);
          setAtRiskMembers(atRiskData || []);
        }
      } catch (error) {
        console.warn('Could not load at-risk members:', error);
        setAtRiskMembers([]);
      }
      
      // Generate comprehensive recent activity
      const recentActivity = generateRecentActivity(data);
      
      // Transform the data to match the expected structure
      const transformedData = {
        organizationId: data.organizationId,
        totalMembers: data.members?.counts?.total || 0,
        recentMembers: data.members?.recent || [],
        upcomingEvents: data.events?.upcoming || [],
        recentDonations: data.donations?.recent || [],
        attendanceStats: data.attendance || {},
        volunteerStats: data.volunteers || {},
        alertStats: data.alerts || {},
        eventsNeedingVolunteers: data.events?.stats?.needingVolunteers || 0,
        tasks: data.tasks?.all || [],
        quickActions: [],
        recentActivity: recentActivity
      };
      
      console.log('MobileDashboard: Transformed data:', transformedData);
      setDashboardData(transformedData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast({
        title: "Error",
        description: "Failed to load dashboard data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, generateRecentActivity]);

  // Pull to refresh functionality
  const handleTouchStart = useCallback((e) => {
    if (window.scrollY === 0) {
      setPullToRefreshY(e.touches[0].clientY);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (window.scrollY === 0 && e.touches[0].clientY > pullToRefreshY) {
      const deltaY = e.touches[0].clientY - pullToRefreshY;
      if (deltaY > 0) {
        setIsPulling(true);
        setPullToRefreshY(e.touches[0].clientY);
      }
    }
  }, [pullToRefreshY]);

  const handleTouchEnd = useCallback(async () => {
    if (isPulling && window.scrollY === 0) {
      setRefreshing(true);
      await loadDashboardData();
      setRefreshing(false);
    }
    setIsPulling(false);
    setPullToRefreshY(0);
  }, [isPulling, loadDashboardData]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Add touch event listeners
  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const handleQuickAction = (action) => {
    navigate(action.href);
  };

  // Advanced Analytics Card Click Handler (same as desktop)
  const handleCardClick = async (cardType) => {
    setSelectedCardType(cardType);
    setIsCardDialogOpen(true);
    
    // Load at-risk members if needed
    if (cardType === 'at-risk-members' && atRiskMembers.length === 0) {
      await loadAtRiskMembers();
    }
  };

  // Load At-Risk Members (same as desktop)
  const loadAtRiskMembers = async () => {
    if (!dashboardData.organizationId) return;
    
    setIsLoadingAtRiskMembers(true);
    try {
      const atRiskData = await SmartInsightsQueries.getAtRiskMembers(dashboardData.organizationId);
      setAtRiskMembers(atRiskData || []);
    } catch (error) {
      console.error('Error loading at-risk members:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load at-risk members data."
      });
    } finally {
      setIsLoadingAtRiskMembers(false);
    }
  };

  // Task Creation Handler (same as desktop)
  const handleCreateTask = (suggestion = null) => {
    // If suggestion is a string, convert it to a proper suggestion object
    let suggestionObject = suggestion;
    if (typeof suggestion === 'string') {
      suggestionObject = {
        title: suggestion,
        description: suggestion,
        content: suggestion
      };
    }
    setCurrentTaskSuggestion(suggestionObject);
    setIsTaskCreationModalOpen(true);
  };

  const handleTaskCreated = () => {
    setIsTaskCreationModalOpen(false);
    setCurrentTaskSuggestion(null);
    loadDashboardData(); // Refresh data after task creation
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Donation trend analysis (same as desktop)
  const trendAnalysis = donationTrendAnalysis || {};
  const donationTrend = trendAnalysis.primaryTrend || 0;
  const canCalculateTrend = trendAnalysis.canCalculateTrend || false;
  const trendDescription = trendAnalysis.trendDescription || '';
  const trendContext = trendAnalysis.trendContext || '';
  const currentWeekOfMonth = trendAnalysis.currentWeekOfMonth || 0;
  const weekAnalyzed = trendAnalysis.weekAnalyzed || currentWeekOfMonth;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const [userFirstName, setUserFirstName] = useState('');

  // Fetch user's first name from members table
  useEffect(() => {
    const fetchUserName = async () => {
      if (!user?.id) return;

      try {
        const { data: member, error } = await supabase
          .from('members')
          .select('firstname')
          .eq('user_id', user.id)
          .single();

        if (!error && member?.firstname) {
          setUserFirstName(member.firstname);
        }
      } catch (error) {
        console.log('Could not fetch user name:', error);
      }
    };

    fetchUserName();
  }, [user?.id]);

  const getUserDisplayName = () => {
    if (userFirstName) return userFirstName;
    
    if (!user) return 'User';
    
    // Try to get first name from user metadata
    const firstName = user.user_metadata?.first_name || user.user_metadata?.firstname;
    if (firstName) return firstName;
    
    // Fallback to extracting from email
    if (user.email) {
      return user.email.split('@')[0] || 'User';
    }
    
    return 'User';
  };

  const todayDateLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric'
  });

  const nextEvent = dashboardData.upcomingEvents?.[0];
  const atRiskMembersForTask = (atRiskMembers || []).map((member) => ({
    id: member.id,
    firstname: member.firstname || '',
    lastname: member.lastname || '',
    email: member.email || '',
    phone: member.phone || '',
    status: member.status || 'active'
  }));
  const hasComparableGivingTrend = canCalculateTrend && typeof donationTrend === 'number';
  const givingTrendPercent = hasComparableGivingTrend ? donationTrend : null;
  const isGivingDeclining = hasComparableGivingTrend && givingTrendPercent < -1;
  const hasUrgentIssues =
    (stats.overdueTasks || 0) > 0 ||
    (atRiskMembers?.length || 0) > 0 ||
    (stats.eventsNeedingVolunteers || 0) > 0 ||
    isGivingDeclining;
  const urgentIssueCount =
    (stats.overdueTasks || 0) +
    (atRiskMembers?.length || 0) +
    (stats.eventsNeedingVolunteers || 0) +
    (isGivingDeclining ? 1 : 0);
  const isEngagementLow = (stats.sundayServiceRate || 0) > 0 && (stats.sundayServiceRate || 0) < 55;
  const hasUpcomingCelebrations = (stats.totalUpcoming || 0) > 0;
  const needsEventPlanning = !nextEvent;
  const upcomingCelebrationEntries = Array.isArray(stats.upcomingMembers) ? stats.upcomingMembers : [];
  const celebrationContactMap = new Map();
  upcomingCelebrationEntries.forEach((member) => {
    if (member?.id && !celebrationContactMap.has(member.id)) {
      celebrationContactMap.set(member.id, {
        id: member.id,
        firstname: member.firstname || '',
        lastname: member.lastname || '',
        email: member.email || '',
        phone: member.phone || '',
        status: member.status || 'active'
      });
    }
  });
  const uniqueCelebrationContacts = Array.from(celebrationContactMap.values());
  const celebrationDetailLines = upcomingCelebrationEntries.slice(0, 20).map((member, index) => {
    const fullName = formatName(member.firstname || '', member.lastname || '') || 'Unknown member';
    const celebrationType = member.celebrationType
      ? `${member.celebrationType.charAt(0).toUpperCase()}${member.celebrationType.slice(1)}`
      : 'Celebration';
    const parsedDate = member.celebrationDate
      ? (member.celebrationDate instanceof Date ? member.celebrationDate : parseISO(member.celebrationDate))
      : null;
    const celebrationDateText = parsedDate && isValid(parsedDate) ? format(parsedDate, 'MMM d') : 'Date TBD';
    const contactText = member.phone || member.email ? ` • ${member.phone || member.email}` : '';
    return `${index + 1}. ${fullName} — ${celebrationType} (${celebrationDateText})${contactText}`;
  });
  const celebrationDetailsText = celebrationDetailLines.length > 0
    ? `Upcoming celebrations:\n${celebrationDetailLines.join('\n')}`
    : 'No celebration contact details were available.';

  const insightHighlights = [
    {
      id: 'engagement',
      label: 'Engagement',
      value: stats.sundayServiceRate >= 70 ? 'Strong' : stats.sundayServiceRate >= 55 ? 'Stable' : 'Needs Attention',
      note: `${stats.sundayServiceRate?.toFixed(0) || 0}% Sunday participation`
    },
    {
      id: 'giving',
      label: 'Giving Trend',
      value: !hasComparableGivingTrend
        ? 'Building Baseline'
        : givingTrendPercent > 1
          ? 'Positive'
          : givingTrendPercent < -1
            ? 'Declining'
            : 'Stable',
      note: !hasComparableGivingTrend
        ? `Week ${weekAnalyzed || currentWeekOfMonth || 1} is still building trend context`
        : trendDescription || `${givingTrendPercent > 0 ? '+' : ''}${givingTrendPercent.toFixed(1)}% week-adjusted trend`
    },
    {
      id: 'care',
      label: 'Community Care',
      value: (atRiskMembers?.length || 0) === 0 ? 'Healthy' : 'Follow-up Needed',
      note: (atRiskMembers?.length || 0) === 0 ? 'No at-risk members detected' : `${atRiskMembers.length} members flagged for care`
    }
  ];

  const focusItems = [];
  if ((stats.overdueTasks || 0) > 0) {
    focusItems.push({
      id: 'overdue',
      title: `${stats.overdueTasks} overdue ${stats.overdueTasks === 1 ? 'task' : 'tasks'}`,
      description: 'Clear blockers and keep ministry workflows moving.',
      icon: Clock4,
      accent: 'text-amber-600',
      bg: 'bg-amber-50 dark:bg-amber-950/30',
      actionLabel: 'Open Tasks',
      onAction: () => navigate('/tasks')
    });
  }
  if ((atRiskMembers?.length || 0) > 0) {
    focusItems.push({
      id: 'pastoral-care',
      title: `${atRiskMembers.length} ${atRiskMembers.length === 1 ? 'member needs' : 'members need'} follow-up`,
      description: 'Pastoral care opportunities identified by engagement signals.',
      icon: Heart,
      accent: 'text-rose-600',
      bg: 'bg-rose-50 dark:bg-rose-950/30',
      actionLabel: 'Create Follow-up Task',
      onAction: () => handleCreateTask({
        title: `Pastoral follow-up: ${atRiskMembers.length} at-risk ${atRiskMembers.length === 1 ? 'member' : 'members'}`,
        description: `Reach out to at-risk members identified from low engagement in the last 60 days.\n\nTotal members to contact: ${atRiskMembers.length}`,
        content: 'Pastoral follow-up for at-risk members',
        priority: 'high',
        category: 'Pastoral Care',
        relatedMembers: atRiskMembersForTask
      })
    });
  }
  if ((stats.eventsNeedingVolunteers || 0) > 0) {
    focusItems.push({
      id: 'volunteers',
      title: `${stats.eventsNeedingVolunteers} events need volunteers`,
      description: 'Assign teams before upcoming gatherings.',
      icon: Users2,
      accent: 'text-violet-600',
      bg: 'bg-violet-50 dark:bg-violet-950/30',
      actionLabel: 'Review Events',
      onAction: () => navigate('/events?needsVolunteers=true&timeWindow=next30days')
    });
  }
  if (isEngagementLow) {
    focusItems.push({
      id: 'engagement',
      title: `Sunday engagement is ${stats.sundayServiceRate?.toFixed(0) || 0}%`,
      description: 'Create a targeted follow-up plan to re-engage members this week.',
      icon: BookOpen,
      accent: 'text-blue-600',
      bg: 'bg-blue-50 dark:bg-blue-950/30',
      actionLabel: 'Create Engagement Task',
      onAction: () => handleCreateTask({
        title: 'Improve Sunday attendance engagement',
        description: `Current Sunday service participation is ${stats.sundayServiceRate?.toFixed(0) || 0}%. Build a follow-up and invitation plan for inactive members.`,
        content: 'Improve Sunday attendance engagement',
        priority: 'medium',
        category: 'Pastoral Care'
      })
    });
  }
  if (isGivingDeclining) {
    focusItems.push({
      id: 'giving-trend',
      title: `Giving trend is down ${Math.abs(givingTrendPercent || 0).toFixed(1)}%`,
      description: trendDescription || 'Compared against an equivalent weekly baseline, not full-month totals.',
      icon: TrendingUp,
      accent: 'text-emerald-600',
      bg: 'bg-emerald-50 dark:bg-emerald-950/30',
      actionLabel: 'Open Donations',
      onAction: () => navigate('/donations')
    });
  }
  if (hasUpcomingCelebrations) {
    focusItems.push({
      id: 'celebrations',
      title: `${stats.totalUpcoming} upcoming celebrations`,
      description: 'Plan intentional outreach for birthdays and anniversaries.',
      icon: Gift,
      accent: 'text-yellow-600',
      bg: 'bg-yellow-50 dark:bg-yellow-950/30',
      actionLabel: 'Plan Outreach Task',
      onAction: () => handleCreateTask({
        title: `Plan outreach for ${stats.totalUpcoming} upcoming celebrations`,
        description: `Prepare calls, messages, and personal touchpoints for ${stats.totalUpcoming} upcoming celebrations in the next 30 days.\n\n${celebrationDetailsText}`,
        content: 'Plan celebration outreach',
        priority: 'medium',
        category: 'Hospitality',
        relatedMembers: uniqueCelebrationContacts
      })
    });
  }
  if (needsEventPlanning) {
    focusItems.push({
      id: 'event-planning',
      title: 'No upcoming events scheduled',
      description: 'Add at least one gathering to keep weekly momentum.',
      icon: Calendar,
      accent: 'text-indigo-600',
      bg: 'bg-indigo-50 dark:bg-indigo-950/30',
      actionLabel: 'Plan Event',
      onAction: () => navigate('/events')
    });
  }
  if (focusItems.length === 0) {
    focusItems.push({
      id: 'all-clear',
      title: 'No urgent blockers right now',
      description: 'Great momentum. Focus on growth and discipleship opportunities.',
      icon: CheckCircle2,
      accent: 'text-emerald-600',
      bg: 'bg-emerald-50 dark:bg-emerald-950/30',
      actionLabel: 'Plan This Week',
      onAction: () => navigate('/events')
    });
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4 space-y-4">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      {/* Pull to refresh indicator */}
      <AnimatePresence>
        {refreshing && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-16 left-0 right-0 z-50 bg-primary text-primary-foreground py-2 text-center"
          >
            <RefreshCw className="h-4 w-4 animate-spin inline mr-2" />
            Refreshing...
          </motion.div>
        )}
      </AnimatePresence>

      <div className="p-4 space-y-6">
        <Card className="overflow-hidden border-0 shadow-lg bg-white dark:bg-slate-800">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{todayDateLabel}</p>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {getGreeting()}, {getUserDisplayName()}
                </h1>
                <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                  {hasUrgentIssues ? `${urgentIssueCount} items need attention today` : 'Everything looks healthy right now'}
                </p>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={loadDashboardData}
                disabled={refreshing}
                aria-label="Refresh dashboard"
                className="bg-white/80 dark:bg-slate-700/80 border-slate-200 dark:border-slate-600"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl bg-white/80 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700 p-2.5 shadow-sm">
                <p className="text-[11px] text-slate-500 dark:text-slate-400">People</p>
                <p className="text-lg font-semibold">{dashboardData.totalMembers}</p>
              </div>
              <div className="rounded-xl bg-white/80 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700 p-2.5 shadow-sm">
                <p className="text-[11px] text-slate-500 dark:text-slate-400">Sunday Rate</p>
                <p className="text-lg font-semibold">{stats.sundayServiceRate?.toFixed(0) || 0}%</p>
              </div>
              <div className="rounded-xl bg-white/80 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700 p-2.5 shadow-sm">
                <p className="text-[11px] text-slate-500 dark:text-slate-400">Total Tasks</p>
                <p className="text-lg font-semibold">{stats.totalTasks || 0}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2">
              {insightHighlights.map((insight) => (
                <div
                  key={insight.id}
                  className="rounded-xl bg-white/80 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700 p-2.5 shadow-sm"
                >
                  <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{insight.label}</p>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">{insight.value}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{insight.note}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Today&apos;s Focus</h2>
          <div className="space-y-2">
            {focusItems.map((item) => (
              <Card key={item.id} className={`${item.bg} border-0 shadow-md`}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-white/80 dark:bg-slate-800/70">
                      <item.icon className={`h-4 w-4 ${item.accent}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-foreground">{item.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                    </div>
                    <Button size="sm" variant="secondary" onClick={item.onAction} className="text-xs">
                      {item.actionLabel}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Quick Launch</h2>
          <div className="grid grid-cols-3 gap-2">
            {quickActions.map((action) => (
              <PermissionFeature key={action.id} permission={action.permission}>
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  className="rounded-xl border-0 shadow-md bg-white dark:bg-slate-800 p-3 text-left w-full"
                  onClick={() => handleQuickAction(action)}
                >
                  <div className={`inline-flex p-2 rounded-lg ${action.color} text-white mb-2`}>
                    <action.icon className="h-4 w-4" />
                  </div>
                  <p className="text-xs font-medium leading-tight">{action.title}</p>
                </motion.button>
              </PermissionFeature>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-white/80 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-1 shadow-sm">
            <TabsTrigger value="focus" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white rounded-lg">Focus</TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white rounded-lg">Analytics</TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-green-500 data-[state=active]:text-white rounded-lg">Events</TabsTrigger>
            <TabsTrigger value="members" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white rounded-lg">People</TabsTrigger>
          </TabsList>

          <TabsContent value="focus" className="space-y-4">
            <Card className="border-0 shadow-md bg-white dark:bg-slate-800">
              <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-indigo-100 dark:from-blue-950/20 dark:to-indigo-900/20 border-b border-blue-200 dark:border-blue-800 rounded-t-xl">
                <CardTitle className="text-lg">Up Next</CardTitle>
              </CardHeader>
              <CardContent>
                {nextEvent ? (
                  <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30 p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-sm truncate">{nextEvent.title || 'Upcoming event'}</p>
                      <Badge variant="secondary">Next Event</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {nextEvent.start_date && isValid(parseISO(nextEvent.start_date))
                        ? format(parseISO(nextEvent.start_date), 'EEE, MMM d • h:mm a')
                        : 'Date not available'}
                    </p>
                    <Button size="sm" variant="outline" onClick={() => navigate('/events')} className="text-xs">
                      Open Events
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No upcoming events scheduled.</p>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-3">
              <Card className="border-0 shadow-md bg-gradient-to-br from-teal-50 to-cyan-100 dark:from-teal-900/20 dark:to-cyan-900/20">
                <CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">Messages</p>
                  <p className="text-xl font-semibold">{stats.totalSMSMessages || 0}</p>
                  <p className="text-xs text-muted-foreground">sent/received</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-md bg-gradient-to-br from-yellow-50 to-orange-100 dark:from-yellow-900/20 dark:to-orange-900/20">
                <CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">Celebrations</p>
                  <p className="text-xl font-semibold">{stats.totalUpcoming || 0}</p>
                  <p className="text-xs text-muted-foreground">in next 30 days</p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card className="border-0 shadow-md bg-white dark:bg-slate-800">
              <CardHeader className="pb-3 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900/20 dark:to-slate-800/20 border-b border-slate-200 dark:border-slate-700 rounded-t-xl">
                <CardTitle className="text-lg">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dashboardData.recentActivity?.map((activity) => (
                  <div 
                    key={activity.id} 
                    className="flex items-center space-x-3 p-2 rounded-lg bg-slate-50/70 dark:bg-slate-900/30 hover:bg-slate-100 dark:hover:bg-slate-900/50 cursor-pointer transition-colors"
                    onClick={() => navigate(activity.href)}
                  >
                    <div className={`p-2 rounded-full ${activity.color} text-white`}>
                      <activity.icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {activity.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {activity.description}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="secondary" className="text-xs">
                        {activity.badge}
                      </Badge>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
                {(!dashboardData.recentActivity || dashboardData.recentActivity.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No recent activity
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Leadership Verse */}
            <LeadershipVerse />
          </TabsContent>

          <TabsContent value="events" className="space-y-4">
            <Card className="border-0 shadow-md bg-white dark:bg-slate-800">
              <CardHeader className="pb-3 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-950/20 dark:to-green-900/20 border-b border-green-200 dark:border-green-800 rounded-t-xl">
                <CardTitle className="text-lg">Upcoming Events</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dashboardData.upcomingEvents?.slice(0, 5).map((event) => (
                  <div key={event?.id || Math.random()} className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-primary" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm text-foreground truncate">
                        {event?.title || 'Untitled Event'}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        {event?.start_date ? format(parseISO(event.start_date), 'MMM d, h:mm a') : 'No date'}
                      </p>
                      {event?.location && (
                        <p className="text-xs text-muted-foreground truncate">
                          📍 {event.location}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/events?id=${event?.id || ''}`)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {(!dashboardData.upcomingEvents || dashboardData.upcomingEvents.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No upcoming events
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-4">
            {/* Advanced Analytics Header */}
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                <BarChart3 className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Advanced Analytics
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Intelligent insights and performance metrics
              </p>
            </div>

            {/* Analytics Cards Grid */}
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
              
              {/* Sunday Service Rate */}
              <Card 
                className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-emerald-200/50 dark:border-emerald-700/50"
                onClick={() => handleCardClick('sunday-service-rate')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-md">
                      <BookOpen className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="font-semibold text-foreground">Sunday Service Rate</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                      {isLoading ? '...' : `${stats.sundayServiceRate.toFixed(0)}%`}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Active members attending Sunday service
                    </p>
                    <div className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                      {stats.sundayServiceRate >= 70 ? 'Excellent engagement' : 
                       stats.sundayServiceRate >= 50 ? 'Good participation' : 'Focus on outreach'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Weekly Trend */}
              <Card 
                className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-blue-200/50 dark:border-blue-700/50"
                onClick={() => handleCardClick('weekly-trend')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-md">
                      <TrendingUp className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="font-semibold text-foreground">Weekly Trend</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                      {isLoading ? '...' : !canCalculateTrend ? `Week ${weekAnalyzed}` : (donationTrend > 0 ? '+' : '') + donationTrend.toFixed(1) + '%'}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {!canCalculateTrend ? trendContext : trendDescription}
                    </p>
                    <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                      {donationTrend > 0 ? 'Strong stewardship' : donationTrend < 0 ? 'Focus on giving' : 'Building momentum'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Event Activity */}
              <Card 
                className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-purple-200/50 dark:border-purple-700/50"
                onClick={() => handleCardClick('event-activity')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
                      <Calendar className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="font-semibold text-foreground">Event Activity</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                      {isLoading ? '...' : stats.eventsThisMonth >= stats.averageEventsPerMonth ? 'At or Above' : 'Below'} Average
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {stats.eventsThisMonth} events this month vs {stats.averageEventsPerMonth} average
                    </p>
                    <div className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                      {stats.eventsNeedingVolunteers > 0 ? `${stats.eventsNeedingVolunteers} events need volunteers` : 'Strong event planning'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Task Management */}
              <Card 
                className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-amber-200/50 dark:border-amber-700/50"
                onClick={() => handleCardClick('task-management')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-md">
                      <CheckSquare className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="font-semibold text-foreground">Task Completion</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                      {isLoading ? '...' : stats.totalTasks > 0 ? `${((stats.totalTasks - stats.overdueTasks) / stats.totalTasks * 100).toFixed(0)}%` : '0%'}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {stats.overdueTasks} overdue tasks need attention
                    </p>
                    <div className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                      {stats.overdueTasks > 0 ? 'Address overdue tasks first' : 'Maintain current workflow'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Volunteer Engagement */}
              <Card 
                className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-violet-200/50 dark:border-violet-700/50"
                onClick={() => handleCardClick('volunteer-engagement')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-xl flex items-center justify-center shadow-md">
                      <Users2 className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="font-semibold text-foreground">Volunteers</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                      {isLoading ? '...' : stats.eventsStillNeedingVolunteers === 0 ? 'Fully Staffed' : 'Needs Volunteers'}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {stats.eventsStillNeedingVolunteers} events need volunteers
                    </p>
                    <div className="text-xs text-violet-600 dark:text-violet-400 font-medium">
                      {stats.eventsStillNeedingVolunteers === 0 ? 'Excellent coverage' : 'Recruit more volunteers'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Visitors */}
              <Card 
                className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-rose-200/50 dark:border-rose-700/50"
                onClick={() => handleCardClick('recent-visitors')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
                      <UserPlus className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="font-semibold text-foreground">Recent Visitors</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                      {isLoading ? '...' : (() => {
                        const thirtyDaysAgo = new Date();
                        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                        return people.filter(person => 
                          person.status === 'visitor' && 
                          new Date(person.createdAt) >= thirtyDaysAgo
                        ).length;
                      })()}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      New visitors in last 30 days
                    </p>
                    <div className="text-xs text-rose-600 dark:text-rose-400 font-medium">
                      {(() => {
                        const thirtyDaysAgo = new Date();
                        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                        const recentVisitors = people.filter(person => 
                          person.status === 'visitor' && 
                          new Date(person.createdAt) >= thirtyDaysAgo
                        ).length;
                        return recentVisitors >= 3 ? 'Strong outreach' : recentVisitors >= 1 ? 'Growing' : 'Focus on invitations';
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Community Health */}
              <Card 
                className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-blue-200/50 dark:border-blue-700/50"
                onClick={() => handleCardClick('community-health')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-md">
                      <Heart className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="font-semibold text-foreground">Community Health</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                      {isLoading ? '...' : stats.inactiveMembers === 0 ? 'Excellent' : stats.inactiveMembers < 5 ? 'Good' : 'Needs Attention'}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {stats.inactiveMembers === 0 ? 'All members engaged' : `${stats.inactiveMembers} members need pastoral care`}
                    </p>
                    <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                      {stats.inactiveMembers > 0 ? 'Schedule follow-up visits' : 'Maintain current connection level'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Family Engagement */}
              <Card 
                className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-green-200/50 dark:border-green-700/50"
                onClick={() => handleCardClick('family-engagement')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-md">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="font-semibold text-foreground">Family Engagement</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                      {isLoading ? '...' : stats.totalFamilies > 0 ? `${Math.round((stats.membersInFamilies / Math.max(stats.totalPeople, 1)) * 100)}%` : '0%'}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {stats.totalFamilies} families • {stats.membersInFamilies} members connected
                    </p>
                    <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                      {(stats.totalPeople - stats.membersInFamilies) > 0 ? `${stats.totalPeople - stats.membersInFamilies} members need family assignment` : 'Strong family connections'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Communication */}
              <Card 
                className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-violet-200/50 dark:border-violet-700/50"
                onClick={() => handleCardClick('communication')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-500 rounded-xl flex items-center justify-center shadow-md">
                      <MessageSquare className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="font-semibold text-foreground">Communication</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                      {isLoading ? '...' : stats.totalSMSConversations > 0 ? 'Active' : 'Growing'}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {stats.totalSMSMessages || 0} messages • {stats.totalSMSConversations || 0} conversations
                    </p>
                    <div className="text-xs text-violet-600 dark:text-violet-400 font-medium">
                      {stats.totalSMSConversations > 0 ? 'Maintain pastoral responsiveness' : 'Encourage two-way communication'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Celebrations */}
              <Card 
                className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-yellow-200/50 dark:border-yellow-700/50"
                onClick={() => handleCardClick('celebrations')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-xl flex items-center justify-center shadow-md">
                      <Gift className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="font-semibold text-foreground">Celebrations</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold bg-gradient-to-r from-yellow-600 to-amber-600 bg-clip-text text-transparent">
                      {isLoading ? '...' : stats.totalUpcoming || 0}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Upcoming celebrations this month
                    </p>
                    <div className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                      {stats.totalUpcoming > 0 ? 'Plan celebration outreach' : 'No upcoming celebrations'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Attendance Diversity */}
              <Card 
                className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-cyan-200/50 dark:border-cyan-700/50"
                onClick={() => handleCardClick('attendance-diversity')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center shadow-md">
                      <Target className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="font-semibold text-foreground">Attendance Diversity</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
                      {isLoading ? '...' : (() => {
                        const eventTypes = [stats.sundayServiceEvents, stats.bibleStudyEvents, stats.fellowshipEvents].filter(count => count > 0);
                        return eventTypes.length >= 3 ? 'Excellent' : eventTypes.length >= 2 ? 'Good' : 'Growing';
                      })()}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {stats.sundayServiceEvents || 0} Sunday • {stats.bibleStudyEvents || 0} Bible Study • {stats.fellowshipEvents || 0} Fellowship
                    </p>
                    <div className="text-xs text-cyan-600 dark:text-cyan-400 font-medium">
                      {(() => {
                        const eventTypes = [stats.sundayServiceEvents, stats.bibleStudyEvents, stats.fellowshipEvents].filter(count => count > 0);
                        return eventTypes.length >= 3 ? 'Strong program diversity' : eventTypes.length >= 2 ? 'Good variety' : 'Expand program offerings';
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* At Risk Members */}
              <Card 
                className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-red-200/50 dark:border-red-700/50"
                onClick={() => handleCardClick('at-risk-members')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl flex items-center justify-center shadow-md">
                      <AlertTriangle className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="font-semibold text-foreground">At Risk Members</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">
                      {isLoading ? '...' : atRiskMembers?.length || 0}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {atRiskMembers?.length === 0 ? 'All members engaged' : `${atRiskMembers?.length || 0} members need pastoral care`}
                    </p>
                    <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                      {(atRiskMembers?.length || 0) > 0 ? 'Schedule follow-up visits' : 'Strong pastoral care'}
                    </div>
                  </div>
                </CardContent>
              </Card>

            </div>
          </TabsContent>

          <TabsContent value="members" className="space-y-4">
            <Card className="border-0 shadow-md bg-white dark:bg-slate-800">
              <CardHeader className="pb-3 bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-950/20 dark:to-purple-900/20 border-b border-purple-200 dark:border-purple-800 rounded-t-xl">
                <CardTitle className="text-lg">Recent People</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dashboardData.recentMembers?.slice(0, 5).map((member) => (
                  <div 
                    key={member?.id || Math.random()} 
                    className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-900/50"
                    onClick={() => navigate(`/members/${member?.id || ''}`)}
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarFallback>
                        {getInitials(member?.firstname || '', member?.lastname || '')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm text-foreground truncate">
                        {formatName(member?.firstname || '', member?.lastname || '')}
                      </h3>
                      <p className="text-xs text-muted-foreground truncate">
                        {member?.email || ''}
                      </p>
                      {member?.phone && (
                        <p className="text-xs text-muted-foreground truncate">
                          📞 {member.phone}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
                {(!dashboardData.recentMembers || dashboardData.recentMembers.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No recent people
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Advanced Analytics Card Dialog - Mobile Optimized */}
        <Dialog open={isCardDialogOpen} onOpenChange={setIsCardDialogOpen}>
          <DialogContent className="w-[95vw] max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedCardType === 'sunday-service-rate' && 'Sunday Service Rate Analysis'}
                {selectedCardType === 'weekly-trend' && 'Weekly Donation Trend Analysis'}
                {selectedCardType === 'event-activity' && 'Event Activity Analysis'}
                {selectedCardType === 'task-management' && 'Task Management Analysis'}
                {selectedCardType === 'volunteer-engagement' && 'Volunteer Engagement Analysis'}
                {selectedCardType === 'recent-visitors' && 'Recent Visitors Analysis'}
                {selectedCardType === 'community-health' && 'Community Health Analysis'}
                {selectedCardType === 'family-engagement' && 'Family Engagement Analysis'}
                {selectedCardType === 'communication' && 'Communication Analysis'}
                {selectedCardType === 'celebrations' && 'Celebrations Analysis'}
                {selectedCardType === 'attendance-diversity' && 'Attendance Diversity Analysis'}
                {selectedCardType === 'at-risk-members' && 'At-Risk Members Analysis'}
              </DialogTitle>
              <DialogDescription>
                Detailed analysis and actionable recommendations for this metric.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Sunday Service Rate Analysis */}
              {selectedCardType === 'sunday-service-rate' && (
                <div className="space-y-4">
                  <div className="text-center p-6 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-lg">
                    <div className="text-4xl font-bold text-emerald-600 mb-2">
                      {stats.sundayServiceRate?.toFixed(0) || 0}%
                    </div>
                    <p className="text-emerald-700 dark:text-emerald-300">
                      Active members attending Sunday service
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Key Insights</h3>
                    <div className="space-y-2 text-sm">
                      <p>• {stats.sundayServiceRate >= 70 ? 'Excellent engagement level' : stats.sundayServiceRate >= 50 ? 'Good participation rate' : 'Focus needed on outreach'}</p>
                      <p>• Regular attendance indicates strong community connection</p>
                      <p>• Consider follow-up with less frequent attendees</p>
                    </div>
                    
                    <h3 className="font-semibold text-lg mt-4">Recommendations</h3>
                    <div className="space-y-2">
                      <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-2 flex-1">
                          <span className="text-emerald-600 font-bold">1.</span>
                          <span className="text-sm">Follow up with inactive members</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={() => handleCreateTask({
                            title: 'Follow up with inactive members',
                            description: 'Reach out to members who haven\'t attended Sunday service recently.',
                            content: 'Follow up with inactive members',
                            priority: 'medium',
                            category: 'Pastoral Care'
                          })}
                        >
                          Create Task
                        </Button>
                      </div>
                      
                      <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-2 flex-1">
                          <span className="text-emerald-600 font-bold">2.</span>
                          <span className="text-sm">Plan engagement activities</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={() => handleCreateTask({
                            title: 'Plan engagement activities',
                            description: 'Create activities to increase Sunday service attendance.',
                            content: 'Plan engagement activities',
                            priority: 'medium',
                            category: 'Ministry'
                          })}
                        >
                          Create Task
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* At-Risk Members Analysis */}
              {selectedCardType === 'at-risk-members' && (
                <div className="space-y-4">
                  <div className="text-center p-6 bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 rounded-lg">
                    <div className="text-4xl font-bold text-red-600 mb-2">
                      {atRiskMembers?.length || 0}
                    </div>
                    <p className="text-red-700 dark:text-red-300">
                      Members needing pastoral attention
                    </p>
                  </div>
                  
                  {atRiskMembers && atRiskMembers.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="font-semibold text-lg">At-Risk Members</h3>
                      <div className="space-y-2">
                        {atRiskMembers.slice(0, 5).map((member) => (
                          <div key={member.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="text-xs">
                                  {getInitials(member.firstname || '', member.lastname || '')}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-sm">
                                  {formatName(member.firstname || '', member.lastname || '')}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {member.reason || 'Needs follow-up'}
                                </p>
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-xs"
                              onClick={() => handleCreateTask({
                                title: `Follow up with ${formatName(member.firstname || '', member.lastname || '')}`,
                                description: `Pastoral follow-up needed: ${member.reason || 'General check-in'}`,
                                content: `Follow up with ${formatName(member.firstname || '', member.lastname || '')}`,
                                priority: 'high',
                                category: 'Pastoral Care'
                              })}
                            >
                              Create Task
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <h3 className="font-semibold text-lg">General Recommendations</h3>
                    <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-start gap-2 flex-1">
                        <span className="text-red-600 font-bold">1.</span>
                        <span className="text-sm">Schedule pastoral visits</span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => handleCreateTask({
                          title: 'Schedule pastoral visits',
                          description: 'Plan and schedule visits for members needing pastoral care.',
                          content: 'Schedule pastoral visits',
                          priority: 'high',
                          category: 'Pastoral Care'
                        })}
                      >
                        Create Task
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Weekly Trend Analysis */}
              {selectedCardType === 'weekly-trend' && (
                <div className="space-y-4">
                  <div className="text-center p-6 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg">
                    <div className="text-4xl font-bold text-blue-600 mb-2">
                      {!canCalculateTrend ? `Week ${weekAnalyzed}` : (donationTrend > 0 ? '+' : '') + donationTrend.toFixed(1) + '%'}
                    </div>
                    <p className="text-blue-700 dark:text-blue-300">
                      {!canCalculateTrend ? trendContext : trendDescription}
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Key Insights</h3>
                    <div className="space-y-2 text-sm">
                      <p>• {donationTrend > 0 ? 'Positive donation growth trend' : donationTrend < 0 ? 'Declining donation trend needs attention' : 'Stable giving pattern'}</p>
                      <p>• This week: ${stats.thisWeek || 0} | Last week: ${stats.lastWeek || 0}</p>
                      <p>• Consistent giving indicates strong stewardship culture</p>
                    </div>
                    
                    <h3 className="font-semibold text-lg mt-4">Recommendations</h3>
                    <div className="space-y-2">
                      {donationTrend < 0 && (
                        <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-start gap-2 flex-1">
                            <span className="text-blue-600 font-bold">1.</span>
                            <span className="text-sm">Plan stewardship campaign</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs"
                            onClick={() => handleCreateTask({
                              title: 'Plan stewardship campaign',
                              description: 'Address declining donation trend with focused stewardship teaching and engagement.',
                              content: 'Plan stewardship campaign',
                              priority: 'high',
                              category: 'Finance'
                            })}
                          >
                            Create Task
                          </Button>
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-2 flex-1">
                          <span className="text-blue-600 font-bold">{donationTrend < 0 ? '2' : '1'}.</span>
                          <span className="text-sm">Send giving statements</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={() => handleCreateTask({
                            title: 'Send giving statements',
                            description: 'Provide members with current year giving statements and tax information.',
                            content: 'Send giving statements',
                            priority: 'medium',
                            category: 'Finance'
                          })}
                        >
                          Create Task
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Event Activity Analysis */}
              {selectedCardType === 'event-activity' && (
                <div className="space-y-4">
                  <div className="text-center p-6 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg">
                    <div className="text-4xl font-bold text-purple-600 mb-2">
                      {stats.eventsThisMonth >= stats.averageEventsPerMonth ? 'Above' : 'Below'} Average
                    </div>
                    <p className="text-purple-700 dark:text-purple-300">
                      {stats.eventsThisMonth} events this month vs {stats.averageEventsPerMonth} average
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Key Insights</h3>
                    <div className="space-y-2 text-sm">
                      <p>• {stats.eventsThisMonth >= stats.averageEventsPerMonth ? 'Strong event programming' : 'Below average event activity'}</p>
                      <p>• {stats.eventsNeedingVolunteers > 0 ? `${stats.eventsNeedingVolunteers} events need volunteers` : 'All events properly staffed'}</p>
                      <p>• Regular events build community engagement</p>
                    </div>
                    
                    <h3 className="font-semibold text-lg mt-4">Recommendations</h3>
                    <div className="space-y-2">
                      {stats.eventsThisMonth < stats.averageEventsPerMonth && (
                        <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-start gap-2 flex-1">
                            <span className="text-purple-600 font-bold">1.</span>
                            <span className="text-sm">Plan additional events</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs"
                            onClick={() => handleCreateTask({
                              title: 'Plan additional events',
                              description: 'Increase event programming to boost community engagement.',
                              content: 'Plan additional events',
                              priority: 'medium',
                              category: 'Ministry'
                            })}
                          >
                            Create Task
                          </Button>
                        </div>
                      )}
                      
                      {stats.eventsNeedingVolunteers > 0 && (
                        <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-start gap-2 flex-1">
                            <span className="text-purple-600 font-bold">{stats.eventsThisMonth < stats.averageEventsPerMonth ? '2' : '1'}.</span>
                            <span className="text-sm">Recruit event volunteers</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs"
                            onClick={() => handleCreateTask({
                              title: 'Recruit event volunteers',
                              description: `${stats.eventsNeedingVolunteers} events need volunteer support.`,
                              content: 'Recruit event volunteers',
                              priority: 'high',
                              category: 'Volunteers'
                            })}
                          >
                            Create Task
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Task Management Analysis */}
              {selectedCardType === 'task-management' && (
                <div className="space-y-4">
                  <div className="text-center p-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg">
                    <div className="text-4xl font-bold text-amber-600 mb-2">
                      {stats.totalTasks > 0 ? `${((stats.totalTasks - stats.overdueTasks) / stats.totalTasks * 100).toFixed(0)}%` : '0%'}
                    </div>
                    <p className="text-amber-700 dark:text-amber-300">
                      On-time completion rate
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Task Overview</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="bg-muted/50 p-3 rounded-lg text-center">
                        <div className="font-bold text-lg">{stats.totalTasks}</div>
                        <div className="text-muted-foreground">Total Tasks</div>
                      </div>
                      <div className="bg-muted/50 p-3 rounded-lg text-center">
                        <div className="font-bold text-lg text-red-600">{stats.overdueTasks}</div>
                        <div className="text-muted-foreground">Overdue</div>
                      </div>
                    </div>
                    
                    <h3 className="font-semibold text-lg mt-4">Recommendations</h3>
                    <div className="space-y-2">
                      {stats.overdueTasks > 0 && (
                        <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-start gap-2 flex-1">
                            <span className="text-amber-600 font-bold">1.</span>
                            <span className="text-sm">Address overdue tasks</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs"
                            onClick={() => navigate('/tasks')}
                          >
                            View Tasks
                          </Button>
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-2 flex-1">
                          <span className="text-amber-600 font-bold">{stats.overdueTasks > 0 ? '2' : '1'}.</span>
                          <span className="text-sm">Review task assignments</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={() => handleCreateTask({
                            title: 'Review task assignments',
                            description: 'Review current task assignments and redistribute if needed.',
                            content: 'Review task assignments',
                            priority: 'medium',
                            category: 'Administration'
                          })}
                        >
                          Create Task
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Volunteer Engagement Analysis */}
              {selectedCardType === 'volunteer-engagement' && (
                <div className="space-y-4">
                  <div className="text-center p-6 bg-gradient-to-r from-violet-50 to-fuchsia-50 dark:from-violet-900/20 dark:to-fuchsia-900/20 rounded-lg">
                    <div className="text-4xl font-bold text-violet-600 mb-2">
                      {stats.eventsStillNeedingVolunteers === 0 ? 'Fully Staffed' : 'Needs Volunteers'}
                    </div>
                    <p className="text-violet-700 dark:text-violet-300">
                      {stats.eventsStillNeedingVolunteers} events need volunteers
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Key Insights</h3>
                    <div className="space-y-2 text-sm">
                      <p>• {stats.eventsStillNeedingVolunteers === 0 ? 'Excellent volunteer coverage' : 'Volunteer recruitment needed'}</p>
                      <p>• Strong volunteer engagement builds community ownership</p>
                      <p>• Regular volunteers are more likely to become leaders</p>
                    </div>
                    
                    <h3 className="font-semibold text-lg mt-4">Recommendations</h3>
                    <div className="space-y-2">
                      {stats.eventsStillNeedingVolunteers > 0 && (
                        <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-start gap-2 flex-1">
                            <span className="text-violet-600 font-bold">1.</span>
                            <span className="text-sm">Launch volunteer recruitment</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs"
                            onClick={() => handleCreateTask({
                              title: 'Launch volunteer recruitment',
                              description: 'Recruit volunteers for upcoming events that need support.',
                              content: 'Launch volunteer recruitment',
                              priority: 'high',
                              category: 'Volunteers'
                            })}
                          >
                            Create Task
                          </Button>
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-2 flex-1">
                          <span className="text-violet-600 font-bold">{stats.eventsStillNeedingVolunteers > 0 ? '2' : '1'}.</span>
                          <span className="text-sm">Appreciate current volunteers</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={() => handleCreateTask({
                            title: 'Appreciate current volunteers',
                            description: 'Plan volunteer appreciation event or recognition program.',
                            content: 'Appreciate current volunteers',
                            priority: 'medium',
                            category: 'Volunteers'
                          })}
                        >
                          Create Task
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Visitors Analysis */}
              {selectedCardType === 'recent-visitors' && (
                <div className="space-y-4">
                  <div className="text-center p-6 bg-gradient-to-r from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20 rounded-lg">
                    <div className="text-4xl font-bold text-rose-600 mb-2">
                      {(() => {
                        const thirtyDaysAgo = new Date();
                        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                        return people.filter(person => 
                          person.status === 'visitor' && 
                          new Date(person.createdAt) >= thirtyDaysAgo
                        ).length;
                      })()}
                    </div>
                    <p className="text-rose-700 dark:text-rose-300">
                      New visitors in last 30 days
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Key Insights</h3>
                    <div className="space-y-2 text-sm">
                      <p>• {(() => {
                        const thirtyDaysAgo = new Date();
                        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                        const recentVisitors = people.filter(person => 
                          person.status === 'visitor' && 
                          new Date(person.createdAt) >= thirtyDaysAgo
                        ).length;
                        return recentVisitors >= 3 ? 'Strong visitor growth' : recentVisitors >= 1 ? 'Steady visitor flow' : 'Need to focus on outreach';
                      })()}</p>
                      <p>• First-time visitors are 7x more likely to return within 2 weeks</p>
                      <p>• Personal follow-up increases retention by 60%</p>
                    </div>
                    
                    <h3 className="font-semibold text-lg mt-4">Recommendations</h3>
                    <div className="space-y-2">
                      <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-2 flex-1">
                          <span className="text-rose-600 font-bold">1.</span>
                          <span className="text-sm">Follow up with recent visitors</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={() => handleCreateTask({
                            title: 'Follow up with recent visitors',
                            description: 'Contact visitors from the last 30 days with personal welcome and invitation.',
                            content: 'Follow up with recent visitors',
                            priority: 'high',
                            category: 'Outreach'
                          })}
                        >
                          Create Task
                        </Button>
                      </div>
                      
                      <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-2 flex-1">
                          <span className="text-rose-600 font-bold">2.</span>
                          <span className="text-sm">Improve visitor experience</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={() => handleCreateTask({
                            title: 'Improve visitor experience',
                            description: 'Review and enhance the first-time visitor experience and welcome process.',
                            content: 'Improve visitor experience',
                            priority: 'medium',
                            category: 'Hospitality'
                          })}
                        >
                          Create Task
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Community Health Analysis */}
              {selectedCardType === 'community-health' && (
                <div className="space-y-4">
                  <div className="text-center p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg">
                    <div className="text-4xl font-bold text-blue-600 mb-2">
                      {stats.inactiveMembers === 0 ? 'Excellent' : stats.inactiveMembers < 5 ? 'Good' : 'Needs Attention'}
                    </div>
                    <p className="text-blue-700 dark:text-blue-300">
                      {stats.inactiveMembers === 0 ? 'All members engaged' : `${stats.inactiveMembers} members need pastoral care`}
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Key Insights</h3>
                    <div className="space-y-2 text-sm">
                      <p>• {stats.inactiveMembers === 0 ? 'Strong community engagement' : 'Some members need reconnection'}</p>
                      <p>• Regular engagement prevents member drift</p>
                      <p>• Personal connection is key to retention</p>
                    </div>
                    
                    <h3 className="font-semibold text-lg mt-4">Recommendations</h3>
                    <div className="space-y-2">
                      {stats.inactiveMembers > 0 && (
                        <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-start gap-2 flex-1">
                            <span className="text-blue-600 font-bold">1.</span>
                            <span className="text-sm">Reach out to inactive members</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs"
                            onClick={() => handleCreateTask({
                              title: 'Reach out to inactive members',
                              description: `Connect with ${stats.inactiveMembers} inactive members to understand their needs.`,
                              content: 'Reach out to inactive members',
                              priority: 'high',
                              category: 'Pastoral Care'
                            })}
                          >
                            Create Task
                          </Button>
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-2 flex-1">
                          <span className="text-blue-600 font-bold">{stats.inactiveMembers > 0 ? '2' : '1'}.</span>
                          <span className="text-sm">Plan community building events</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={() => handleCreateTask({
                            title: 'Plan community building events',
                            description: 'Organize events to strengthen community bonds and engagement.',
                            content: 'Plan community building events',
                            priority: 'medium',
                            category: 'Ministry'
                          })}
                        >
                          Create Task
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Family Engagement Analysis */}
              {selectedCardType === 'family-engagement' && (
                <div className="space-y-4">
                  <div className="text-center p-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg">
                    <div className="text-4xl font-bold text-green-600 mb-2">
                      {stats.totalFamilies > 0 ? `${Math.round((stats.membersInFamilies / Math.max(stats.totalPeople, 1)) * 100)}%` : '0%'}
                    </div>
                    <p className="text-green-700 dark:text-green-300">
                      {stats.totalFamilies} families • {stats.membersInFamilies} members connected
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Key Insights</h3>
                    <div className="space-y-2 text-sm">
                      <p>• {(stats.totalPeople - stats.membersInFamilies) > 0 ? `${stats.totalPeople - stats.membersInFamilies} members need family assignment` : 'All members have family connections'}</p>
                      <p>• Family connections strengthen community bonds</p>
                      <p>• Family ministry increases long-term retention</p>
                    </div>
                    
                    <h3 className="font-semibold text-lg mt-4">Recommendations</h3>
                    <div className="space-y-2">
                      {(stats.totalPeople - stats.membersInFamilies) > 0 && (
                        <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-start gap-2 flex-1">
                            <span className="text-green-600 font-bold">1.</span>
                            <span className="text-sm">Connect unassigned members</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs"
                            onClick={() => handleCreateTask({
                              title: 'Connect unassigned members',
                              description: `Help ${stats.totalPeople - stats.membersInFamilies} members find family connections.`,
                              content: 'Connect unassigned members',
                              priority: 'medium',
                              category: 'Family Ministry'
                            })}
                          >
                            Create Task
                          </Button>
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-2 flex-1">
                          <span className="text-green-600 font-bold">{(stats.totalPeople - stats.membersInFamilies) > 0 ? '2' : '1'}.</span>
                          <span className="text-sm">Plan family ministry events</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={() => handleCreateTask({
                            title: 'Plan family ministry events',
                            description: 'Organize events that strengthen family bonds and connections.',
                            content: 'Plan family ministry events',
                            priority: 'medium',
                            category: 'Family Ministry'
                          })}
                        >
                          Create Task
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Communication Analysis */}
              {selectedCardType === 'communication' && (
                <div className="space-y-4">
                  <div className="text-center p-6 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-lg">
                    <div className="text-4xl font-bold text-violet-600 mb-2">
                      {stats.totalSMSConversations > 0 ? 'Active' : 'Growing'}
                    </div>
                    <p className="text-violet-700 dark:text-violet-300">
                      {stats.totalSMSMessages || 0} messages • {stats.totalSMSConversations || 0} conversations
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Key Insights</h3>
                    <div className="space-y-2 text-sm">
                      <p>• {stats.totalSMSConversations > 0 ? 'Good two-way communication flow' : 'Opportunity to increase engagement'}</p>
                      <p>• Personal communication builds stronger relationships</p>
                      <p>• Quick response times improve member satisfaction</p>
                    </div>
                    
                    <h3 className="font-semibold text-lg mt-4">Recommendations</h3>
                    <div className="space-y-2">
                      {stats.totalSMSConversations === 0 && (
                        <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-start gap-2 flex-1">
                            <span className="text-violet-600 font-bold">1.</span>
                            <span className="text-sm">Encourage member communication</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs"
                            onClick={() => handleCreateTask({
                              title: 'Encourage member communication',
                              description: 'Promote two-way communication channels with members.',
                              content: 'Encourage member communication',
                              priority: 'medium',
                              category: 'Communication'
                            })}
                          >
                            Create Task
                          </Button>
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-2 flex-1">
                          <span className="text-violet-600 font-bold">{stats.totalSMSConversations === 0 ? '2' : '1'}.</span>
                          <span className="text-sm">Review communication strategy</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={() => handleCreateTask({
                            title: 'Review communication strategy',
                            description: 'Assess and improve current communication methods and response times.',
                            content: 'Review communication strategy',
                            priority: 'low',
                            category: 'Communication'
                          })}
                        >
                          Create Task
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Celebrations Analysis */}
              {selectedCardType === 'celebrations' && (
                <div className="space-y-4">
                  <div className="text-center p-6 bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-lg">
                    <div className="text-4xl font-bold text-yellow-600 mb-2">
                      {stats.totalUpcoming || 0}
                    </div>
                    <p className="text-yellow-700 dark:text-yellow-300">
                      Upcoming celebrations this month
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Key Insights</h3>
                    <div className="space-y-2 text-sm">
                      <p>• {stats.totalUpcoming > 0 ? 'Good celebration planning' : 'Opportunity for more celebrations'}</p>
                      <p>• Celebrations strengthen community bonds</p>
                      <p>• Personal recognition increases member engagement</p>
                    </div>
                    
                    <h3 className="font-semibold text-lg mt-4">Recommendations</h3>
                    <div className="space-y-2">
                      {stats.totalUpcoming === 0 && (
                        <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-start gap-2 flex-1">
                            <span className="text-yellow-600 font-bold">1.</span>
                            <span className="text-sm">Plan celebration events</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs"
                            onClick={() => handleCreateTask({
                              title: 'Plan celebration events',
                              description: 'Organize birthday, anniversary, and milestone celebrations.',
                              content: 'Plan celebration events',
                              priority: 'medium',
                              category: 'Hospitality'
                            })}
                          >
                            Create Task
                          </Button>
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-2 flex-1">
                          <span className="text-yellow-600 font-bold">{stats.totalUpcoming === 0 ? '2' : '1'}.</span>
                          <span className="text-sm">Create celebration calendar</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={() => handleCreateTask({
                            title: 'Create celebration calendar',
                            description: 'Develop a systematic approach to tracking and celebrating member milestones.',
                            content: 'Create celebration calendar',
                            priority: 'low',
                            category: 'Administration'
                          })}
                        >
                          Create Task
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Attendance Diversity Analysis */}
              {selectedCardType === 'attendance-diversity' && (
                <div className="space-y-4">
                  <div className="text-center p-6 bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 rounded-lg">
                    <div className="text-4xl font-bold text-cyan-600 mb-2">
                      {(() => {
                        const eventTypes = [stats.sundayServiceEvents, stats.bibleStudyEvents, stats.fellowshipEvents].filter(count => count > 0);
                        return eventTypes.length >= 3 ? 'Excellent' : eventTypes.length >= 2 ? 'Good' : 'Growing';
                      })()}
                    </div>
                    <p className="text-cyan-700 dark:text-cyan-300">
                      {stats.sundayServiceEvents || 0} Sunday • {stats.bibleStudyEvents || 0} Bible Study • {stats.fellowshipEvents || 0} Fellowship
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Key Insights</h3>
                    <div className="space-y-2 text-sm">
                      <p>• {(() => {
                        const eventTypes = [stats.sundayServiceEvents, stats.bibleStudyEvents, stats.fellowshipEvents].filter(count => count > 0);
                        return eventTypes.length >= 3 ? 'Strong program diversity' : eventTypes.length >= 2 ? 'Good variety' : 'Need more program types';
                      })()}</p>
                      <p>• Diverse programming meets different spiritual needs</p>
                      <p>• Variety attracts different age groups and interests</p>
                    </div>
                    
                    <h3 className="font-semibold text-lg mt-4">Recommendations</h3>
                    <div className="space-y-2">
                      {(() => {
                        const eventTypes = [stats.sundayServiceEvents, stats.bibleStudyEvents, stats.fellowshipEvents].filter(count => count > 0);
                        return eventTypes.length < 3;
                      })() && (
                        <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-start gap-2 flex-1">
                            <span className="text-cyan-600 font-bold">1.</span>
                            <span className="text-sm">Expand program offerings</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs"
                            onClick={() => handleCreateTask({
                              title: 'Expand program offerings',
                              description: 'Add more diverse event types to meet different member needs.',
                              content: 'Expand program offerings',
                              priority: 'medium',
                              category: 'Ministry'
                            })}
                          >
                            Create Task
                          </Button>
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-2 flex-1">
                          <span className="text-cyan-600 font-bold">{(() => {
                            const eventTypes = [stats.sundayServiceEvents, stats.bibleStudyEvents, stats.fellowshipEvents].filter(count => count > 0);
                            return eventTypes.length < 3 ? '2' : '1';
                          })()}.</span>
                          <span className="text-sm">Survey member interests</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={() => handleCreateTask({
                            title: 'Survey member interests',
                            description: 'Gather feedback on what types of events and programs members want.',
                            content: 'Survey member interests',
                            priority: 'low',
                            category: 'Research'
                          })}
                        >
                          Create Task
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Task Creation Modal */}
        <TaskCreationModal
          isOpen={isTaskCreationModalOpen}
          onClose={() => setIsTaskCreationModalOpen(false)}
          suggestion={currentTaskSuggestion}
          onTaskCreated={handleTaskCreated}
        />
      </div>
    </div>
  );
}